// src/components/ReviewMode/AnswerInputs/AudioQuestion.jsx
import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography, Button, Paper } from '@mui/material';
import { styled, keyframes } from '@mui/system';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import SpeedIcon from '@mui/icons-material/Speed';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SendIcon from '@mui/icons-material/Send';
import ReorderTokensInput from './ReorderTokensInput';
import TextAnswerInput from './TextAnswerInput';

// Animated waveform effect
const wave = keyframes`
  0% { height: 5px; }
  50% { height: 20px; }
  100% { height: 5px; }
`;

// Styled components
const AudioContainer = styled(Box)(({ theme }) => ({
  padding: '24px',
  borderRadius: '16px',
  backgroundColor: 'rgba(250, 250, 250, 0.7)',
  boxShadow: '0 8px 30px rgba(0, 0, 0, 0.05)',
  marginBottom: '24px',
}));

const PlayerContainer = styled(Paper)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: '16px',
  borderRadius: '12px',
  backgroundColor: '#483fc3',
  color: 'white',
  marginBottom: '24px',
}));

const PlayButton = styled(Button)(({ theme }) => ({
  minWidth: '50px',
  height: '50px',
  borderRadius: '50%',
  padding: 0,
  marginRight: '16px',
  backgroundColor: 'rgba(255, 255, 255, 0.2)',
  color: 'white',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
  },
}));

const SpeedButton = styled(Button)(({ theme }) => ({
  minWidth: '40px',
  height: '30px',
  borderRadius: '15px',
  padding: '0 10px',
  marginLeft: '16px',
  backgroundColor: 'rgba(255, 255, 255, 0.2)',
  color: 'white',
  fontSize: '0.75rem',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
  },
}));

const WaveformContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  height: '30px',
  flex: 1,
});

const WaveBar = styled(Box)(({ active, index, isPlaying }) => ({
  width: '4px',
  height: '5px',
  backgroundColor: 'white',
  margin: '0 2px',
  borderRadius: '2px',
  opacity: active ? 1 : 0.4,
  animation: isPlaying && active ? `${wave} 1s ease-in-out infinite` : 'none',
  animationDelay: `${index * 0.05}s`,
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  marginTop: '16px',
  borderRadius: '12px',
  padding: '8px 16px',
  fontWeight: '600',
  boxShadow: '0 4px 12px rgba(63, 81, 181, 0.2)',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 16px rgba(63, 81, 181, 0.3)',
  },
}));

/**
 * AudioQuestion component for audio-based review questions
 * Integrates with ReorderTokensInput for assisted mode or TextAnswerInput for challenging mode
 */
const AudioQuestion = ({ 
  audioData, 
  tokens, 
  question, 
  mode = 'transcribe', // 'transcribe' or 'translate'
  waitingToProceed,
  onProceed,
  onSubmitAnswer,
  correctAnswer 
}) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [waveformBars] = useState(Array.from({ length: 30 }, (_, i) => i));
  const [activeWaveBars, setActiveWaveBars] = useState(() => {
    // Randomly determine which bars are "active" in the waveform
    return waveformBars.map(() => Math.random() > 0.4);
  });

  // Playback speed options
  const speeds = [0.5, 0.75, 1];

  // Determine if we're in challenging mode (no tokens) or assisted mode (has tokens)
  const isChallengingMode = !tokens || tokens.length === 0;

  // Handle audio playback
  const togglePlayback = () => {
    if (!audioRef.current) return;
    
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play().catch(error => {
        console.error("Error playing audio:", error);
      });
    }
  };

  // Change playback speed
  const handleSpeedChange = () => {
    if (!audioRef.current) return;
    
    const currentIndex = speeds.indexOf(playbackSpeed);
    const nextSpeed = speeds[(currentIndex + 1) % speeds.length];
    audioRef.current.playbackRate = nextSpeed;
    setPlaybackSpeed(nextSpeed);
  };

  // Manage audio events
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => setIsPlaying(false);

    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handleEnded);

    // Setup keyboard shortcuts for audio control
    const handleKeyPress = (event) => {
      // Spacebar to play/pause
      if (event.code === 'Space' && !event.target.matches('input, textarea, [contenteditable]')) {
        event.preventDefault();
        togglePlayback();
      }
      
      // 'S' key to change speed
      if (event.key === 's' || event.key === 'S') {
        handleSpeedChange();
      }
    };

    window.addEventListener('keydown', handleKeyPress);

    return () => {
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('ended', handleEnded);
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, [playbackSpeed, speeds]);

  // Helper to get instruction text based on mode
  const getInstructionText = () => {
    switch (mode) {
      case 'transcribe':
        return isChallengingMode 
          ? "Listen to the audio and transcribe what you hear"
          : "Listen to the audio and arrange the tokens to transcribe what you hear";
      case 'translate':
        return isChallengingMode
          ? "Listen to the audio and translate what you hear"
          : "Listen to the audio and arrange the tokens to translate what you hear";
      default:
        return isChallengingMode
          ? "Listen to the audio and type your answer"
          : "Listen to the audio and arrange the tokens accordingly";
    }
  };

  // Fallback if audio data is unavailable
  if (!audioData) {
    return (
      <Box sx={{ mt: 3 }}>
        <Typography variant="body1" color="error">
          Audio unavailable for this question. Please try answering with the text-based interface.
        </Typography>
        {isChallengingMode ? (
          <TextAnswerInput
            onSubmitAnswer={onSubmitAnswer}
            waitingToProceed={waitingToProceed}
            onProceed={onProceed}
            audioData={null}
            question={question}
            hideAudioPlayer={true}
          />
        ) : (
          <ReorderTokensInput
            tokens={tokens}
            onSubmitAnswer={onSubmitAnswer}
            waitingToProceed={waitingToProceed}
            onProceed={onProceed}
            correctAnswer={correctAnswer}
            hideAudioPlayer={true}
          />
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 3 }}>
      <AudioContainer>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
          {question || getInstructionText()}
        </Typography>
        
        <PlayerContainer>
          {/* Hidden audio element */}
          <audio
            ref={audioRef}
            src={audioData}
            style={{ display: 'none' }}
          />
          
          {/* Play/Pause button */}
          <PlayButton onClick={togglePlayback}>
            {isPlaying ? <PauseCircleOutlineIcon /> : <PlayCircleOutlineIcon />}
          </PlayButton>
          
          {/* Waveform visualization */}
          <WaveformContainer>
            {waveformBars.map((index) => (
              <WaveBar 
                key={index} 
                active={activeWaveBars[index]}
                index={index}
                isPlaying={isPlaying}
              />
            ))}
          </WaveformContainer>
          
          {/* Playback speed control */}
          <SpeedButton onClick={handleSpeedChange} startIcon={<SpeedIcon sx={{ fontSize: '16px' }} />}>
            {playbackSpeed}x
          </SpeedButton>
        </PlayerContainer>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Press <strong>Space</strong> to play/pause, <strong>S</strong> to change speed
        </Typography>
      </AudioContainer>
      
      {/* Conditional rendering based on challenging mode vs assisted mode */}
      {isChallengingMode ? (
        // Challenging mode: render text input for free-form answers
        <TextAnswerInput
          onSubmitAnswer={onSubmitAnswer}
          waitingToProceed={waitingToProceed}
          onProceed={onProceed}
          question={question}
          hideAudioPlayer={true}
        />
      ) : (
        // Assisted mode: render token reordering component
        <ReorderTokensInput
          tokens={tokens}
          onSubmitAnswer={onSubmitAnswer}
          waitingToProceed={waitingToProceed}
          onProceed={onProceed}
          correctAnswer={correctAnswer}
          question={question}
          hideAudioPlayer={true}
        />
      )}
      
      {/* Additional control if needed */}
      {waitingToProceed && (
        <SubmitButton 
          variant="contained" 
          color="primary" 
          onClick={onProceed}
          endIcon={<ArrowForwardIcon />}
        >
          Continue
        </SubmitButton>
      )}
    </Box>
  );
};

export default AudioQuestion;